import { useEffect } from 'react'
import { useSupabaseClient } from 'src/api/supabase'
import { useAuth } from 'src/shared/hooks/authProvider'

const HEARTBEAT_MS = 20_000

const isBrowserTabVisible = (): boolean =>
  typeof document === 'undefined' || document.visibilityState === 'visible'

/**
 * While the request chat sidebar is open and visible, keep a presence row
 * fresh so delayed team-message emails are cancelled for this user.
 */
export const useRequestChatPresence = ({
  quotePublicId,
  enabled,
}: {
  quotePublicId?: string
  enabled: boolean
}) => {
  const supabase = useSupabaseClient()
  const { session } = useAuth()
  const userId = session?.user?.id
  const isAnonymous = session?.user?.is_anonymous === true

  useEffect(() => {
    if (!enabled || !quotePublicId || !userId || isAnonymous) return

    let cancelled = false

    const ping = async () => {
      if (cancelled || !isBrowserTabVisible()) return
      const { error } = await supabase.from('request_presence').upsert({
        quote_public_id: quotePublicId,
        user_id: userId,
        last_seen_at: new Date().toISOString(),
      })
      if (error) {
        console.error('[presence] failed to record chat open:', error.message)
      }
    }

    void ping()
    const interval = window.setInterval(() => {
      void ping()
    }, HEARTBEAT_MS)

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void ping()
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      cancelled = true
      window.clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [enabled, isAnonymous, quotePublicId, supabase, userId])
}
